/**
 * Schémas Zod de l'outil Scribe (outils.scribe, lot 1 du 30/10/2026). Même convention que schemas.ts : l'API valide ses
 * entrées avec ces schémas, les formulaires du front aussi ; types déduits. Règles : outils/scribe.ts.
 * Une réunion n'est visible que de son auteur jusqu'à sa validation ; validée, elle est lisible (lecture seule) de qui voit
 * un de ses objets rattachés confirmés (décision R3, option 2, 02/11/2026). L'audio ne se relit pas ; la transcription
 * est effacée à la validation : seul le compte-rendu reste.
 */
import { z } from 'zod';
import { LANGUES } from './langues';
import { schemaDateIso } from './schemas';
import { RESSOURCES_LIEES_APPEL, STATUTS_LIEN } from './service/telephonie';
import {
  BORNES_SCRIBE,
  DUREE_PARTIE_MS,
  ERREUR_COMPTE_RENDU,
  ETATS_AGENDA,
  ETATS_REUNION,
  NIVEAU_COMPTE_RENDU_DEFAUT,
  NIVEAUX_COMPTE_RENDU,
  PARTIES_PAR_SEQUENCE_MAX,
  SEQUENCES_MAX,
  TAILLE_MAX_PARTIE,
  TYPES_INVITE_AGENDA,
  TYPES_PARTIE_SCRIBE,
} from './outils/scribe';

// ------------------------------------------------------------------ briques
export const schemaEtatReunion = z.enum(ETATS_REUNION);
export const schemaNiveauCompteRendu = z.enum(NIVEAUX_COMPTE_RENDU);

const titre = z.string().trim().min(1, 'Obligatoire').max(BORNES_SCRIBE.titre);
const element = z.string().trim().min(1).max(BORNES_SCRIBE.element);
const schemaJour = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'Date AAAA-MM-JJ attendue');

// ------------------------------------------------------------------ réunion
/** Adresse e-mail d'un invité : en minuscules, comme les destinataires de la messagerie. */
const adresseInvite = z.string().trim().toLowerCase().pipe(z.email().max(254));

/**
 * Invité de la réunion Outlook choisie au démarrage (lot 3 « agenda Outlook ») : nom affiché (vide si Outlook n'en a pas)
 * et adresse. Gardé avec la réunion pour le compte-rendu (participants) et le brouillon d'e-mail (destinataires).
 */
export const schemaInviteReunion = z.object({
  nom: z.string().trim().max(BORNES_SCRIBE.nomCite),
  email: adresseInvite,
});

/**
 * Début d'un enregistrement : titre, langue du compte-rendu (langue d'AGORA de l'utilisateur au démarrage), niveau de
 * détail, et la case « Participants informés » cochée (obligatoire, journalisée). Lot 3 « agenda Outlook » : invités de
 * la réunion Outlook choisie (« Utiliser » du panneau) et `titreAuto` (le titre est encore celui proposé par le panneau :
 * le traitement le remplace par le titre que l'IA propose).
 */
export const schemaReunionNouvelle = z.object({
  titre,
  langue: z.enum(LANGUES),
  niveau: schemaNiveauCompteRendu.default(NIVEAU_COMPTE_RENDU_DEFAUT),
  participantsInformes: z.literal(true, { message: 'Les participants doivent être informés' }),
  invites: z.array(schemaInviteReunion).max(BORNES_SCRIBE.invites).default([]),
  titreAuto: z.boolean().default(false),
});

/** Action décidée en réunion : responsable et échéance quand la réunion les cite. */
export const schemaActionCompteRendu = z.object({
  libelle: element,
  responsable: z.string().trim().max(BORNES_SCRIBE.responsable).nullable().default(null),
  echeance: schemaJour.nullable().default(null),
});

/** Sujet abordé (niveaux Détaillé et Exhaustif) : un titre court et son déroulé. */
export const schemaSectionCompteRendu = z.object({
  titre: z.string().trim().min(1).max(BORNES_SCRIBE.titre),
  contenu: z.string().trim().min(1).max(BORNES_SCRIBE.section),
});

/** Personne ou organisation nommée pendant la réunion. */
const nomCite = z.string().trim().min(1).max(BORNES_SCRIBE.nomCite);

/**
 * Compte-rendu proposé par l'IA du socle, puis relu par l'auteur. `sections` : vide au niveau Essentiel.
 * `participants` : invités de la réunion Outlook choisie au démarrage (lot 3), puis personnes que la transcription nomme
 * explicitement comme présentes ou prenant la parole, jamais devinées ; `organisations` : sociétés, clients ou sites cités
 * par leur nom (suggestions de rattachement, cherchées par l'écran avec les droits de l'utilisateur). Absents des
 * comptes-rendus plus anciens : listes vides.
 */
export const schemaCompteRendu = z.object({
  resume: z.string().trim().max(BORNES_SCRIBE.resume),
  sections: z.array(schemaSectionCompteRendu).max(BORNES_SCRIBE.sections).default([]),
  participants: z.array(nomCite).max(BORNES_SCRIBE.participants).default([]),
  decisions: z.array(element).max(BORNES_SCRIBE.elements),
  actions: z.array(schemaActionCompteRendu).max(BORNES_SCRIBE.elements),
  pointsOuverts: z.array(element).max(BORNES_SCRIBE.elements),
  organisations: z.array(nomCite).max(BORNES_SCRIBE.nomsCites).default([]),
});

/** Relecture : titre et compte-rendu (réunion « à valider » seulement). */
export const schemaReunionModification = z
  .object({ titre, compteRendu: schemaCompteRendu })
  .partial()
  .refine((v) => Object.values(v).some((x) => x !== undefined), { message: 'Aucune modification' });

/**
 * Réunion telle que listée ou ouverte : par son auteur, ou – validée – par qui voit un de ses objets rattachés
 * confirmés (`lectureSeule`, nom de l'`auteur` ; jamais d'erreur interne ni de transcription).
 */
export const schemaReunion = z.object({
  id: z.string(),
  titre: z.string(),
  etat: schemaEtatReunion,
  niveau: schemaNiveauCompteRendu,
  langue: z.enum(LANGUES),
  /** Langue de la réunion détectée par la transcription ; null avant. */
  langueDetectee: z.string().nullable(),
  debut: schemaDateIso,
  fin: schemaDateIso.nullable(),
  /** Durée enregistrée (somme des parties reçues), en secondes. */
  dureeS: z.number().int(),
  participantsInformesLe: schemaDateIso,
  compteRendu: schemaCompteRendu.nullable(),
  /** Code d'erreur du traitement (erreurs.scribe.<code>) ; null sans erreur. */
  erreur: z.string().nullable(),
  valideLe: schemaDateIso.nullable(),
  modifieLe: schemaDateIso,
  /**
   * Réunion validée d'un autre auteur, partagée par ses rattachements (décision R3) : lecture seule. Absent (ou faux) :
   * réunion de l'utilisateur.
   */
  lectureSeule: z.boolean().optional(),
  /** Nom de l'auteur d'une réunion partagée (son e-mail s'il n'est pas connu) ; absent pour ses propres réunions. */
  auteur: z.string().optional(),
  /**
   * Invités de la réunion Outlook choisie au démarrage (destinataires du brouillon d'e-mail) : pour l'auteur seulement,
   * jamais pour le lecteur d'une réunion partagée ; absent sans réunion Outlook ou une fois la réunion supprimée.
   */
  invites: z.array(schemaInviteReunion).optional(),
  /**
   * Transcription gardée d'une réunion en erreur dont la transcription avait réussi (compte-rendu en échec) : montrée en
   * lecture à son auteur, sur la page de la réunion, et reprise par « Relancer le compte-rendu ». Absente partout
   * ailleurs (liste des réunions, autres états, lecteurs d'une réunion partagée).
   */
  transcription: z.string().optional(),
});

/**
 * « Relancer le compte-rendu » : réunion en erreur `compte_rendu_echec` dont la transcription est gardée. Le niveau de
 * détail peut être choisi de nouveau ; absent, celui de la réunion.
 */
export const schemaRelanceCompteRendu = z.object({ niveau: schemaNiveauCompteRendu.optional() });

/** La réunion permet-elle « Relancer le compte-rendu » ? Même règle que l'API (droits à part). */
export const compteRenduRelancable = (r: Pick<z.infer<typeof schemaReunion>, 'etat' | 'erreur' | 'transcription' | 'lectureSeule'>) =>
  r.etat === 'erreur' && r.erreur === ERREUR_COMPTE_RENDU && !!r.transcription?.trim() && !r.lectureSeule;

export const schemaRechercheReunions = z.object({
  /**
   * Réunions rattachées à cet objet (section « Réunions » d'une fiche) : les deux ensemble. Sans objet : les réunions
   * de l'utilisateur seulement ; avec : les siennes et les réunions validées partagées par ce rattachement.
   */
  ressource: z.enum(RESSOURCES_LIEES_APPEL).optional(),
  objetId: z.uuid().optional(),
  etat: schemaEtatReunion.optional(),
  limite: z.coerce.number().int().min(1).max(100).default(50),
});

// ------------------------------------------------------------------ parties d'enregistrement
/** Demande de dépôt d'une partie : position (séquence, numéro), format et taille, durée enregistrée. */
export const schemaDemandePartie = z.object({
  sequence: z.number().int().min(0).max(SEQUENCES_MAX - 1),
  numero: z.number().int().min(0).max(PARTIES_PAR_SEQUENCE_MAX - 1),
  type: z.enum(TYPES_PARTIE_SCRIBE),
  taille: z.number().int().min(1).max(TAILLE_MAX_PARTIE),
  dureeMs: z.number().int().min(1).max(DUREE_PARTIE_MS * 2),
});

/** Plafond d'usage de la semaine (lundi 00:00, heure de Paris). */
export const schemaQuotaScribe = z.object({
  semaineDu: schemaJour,
  utiliseS: z.number().int(),
  plafondS: z.number().int(),
});

// ------------------------------------------------------------------ agenda Outlook (lot 3)
/** Personne invitée à une réunion Outlook : organisateur, invité requis ou facultatif (salles et ressources retirées). */
export const schemaInviteAgenda = schemaInviteReunion.extend({ type: z.enum(TYPES_INVITE_AGENDA) });

/** Réunion de l'agenda Outlook en cours ou qui commence bientôt (rien n'en est gardé par AGORA). */
export const schemaEvenementAgenda = z.object({
  id: z.string(),
  sujet: z.string(),
  debut: schemaDateIso,
  fin: schemaDateIso,
  /** Réunion Teams (ou autre réunion en ligne). */
  enLigne: z.boolean(),
  /** Organisateur d'abord, puis les invités ; sans doublon d'adresse. */
  invites: z.array(schemaInviteAgenda).max(BORNES_SCRIBE.invites),
});

/** Réponse de GET /v1/outils/scribe/agenda : état de l'accès à l'agenda et réunions proposées. */
export const schemaAgendaScribe = z.object({
  etat: z.enum(ETATS_AGENDA),
  evenements: z.array(schemaEvenementAgenda),
});

export type InviteReunion = z.infer<typeof schemaInviteReunion>;
export type InviteAgenda = z.infer<typeof schemaInviteAgenda>;
export type EvenementAgenda = z.infer<typeof schemaEvenementAgenda>;
export type AgendaScribe = z.infer<typeof schemaAgendaScribe>;
export type ReunionNouvelle = z.input<typeof schemaReunionNouvelle>;
export type ReunionModification = z.input<typeof schemaReunionModification>;
export type RelanceCompteRendu = z.input<typeof schemaRelanceCompteRendu>;
export type CompteRendu = z.infer<typeof schemaCompteRendu>;
export type SectionCompteRendu = z.infer<typeof schemaSectionCompteRendu>;
export type ActionCompteRendu = z.infer<typeof schemaActionCompteRendu>;
export type Reunion = z.infer<typeof schemaReunion>;
export type RechercheReunions = z.input<typeof schemaRechercheReunions>;
export type DemandePartie = z.infer<typeof schemaDemandePartie>;
export type QuotaScribe = z.infer<typeof schemaQuotaScribe>;

// ------------------------------------------------------------------ rattachements (menu « Affecter », lot 2)
/**
 * Objets auxquels une réunion se rattache : ceux du registre des objets affectables du socle (api/src/liens/objets.ts),
 * les mêmes que pour un appel. `ia` : objet cité dans la réunion, proposé par le compte-rendu ; `manuel` : choisi par
 * l'auteur.
 */
export const ORIGINES_LIEN_REUNION = ['ia', 'manuel'] as const;

export const schemaLienReunion = z.object({
  ressource: z.enum(RESSOURCES_LIEES_APPEL),
  objetId: z.string(),
  /** null : objet devenu invisible pour l'auteur. */
  code: z.string().nullable(),
  libelle: z.string().nullable(),
  origine: z.enum(ORIGINES_LIEN_REUNION),
  statut: z.enum(STATUTS_LIEN),
});

/** Menu « Affecter » : rattacher (actif) ou retirer (rattachement rejeté, jamais effacé). */
export const schemaAffectationReunion = z.object({
  ressource: z.enum(RESSOURCES_LIEES_APPEL),
  objetId: z.uuid(),
  actif: z.boolean(),
});

export type LienReunion = z.infer<typeof schemaLienReunion>;
export type AffectationReunion = z.infer<typeof schemaAffectationReunion>;
